import React, {useCallback, useEffect, useRef, useState} from 'react';
import {ReactComponent as GradiantLine} from '../../../../assests/svg/gradiantLine.svg';
import {
  Box,
  Typography,
  InputAdornment,
  SelectChangeEvent,
  FormControlLabel,
  Radio,
  Link,
} from '@mui/material';
import {ReactComponent as AddIcon} from '../../../../assests/svg/plus-icon-success.svg';
import {t} from 'i18next';
import {
  EssentialProcessingInstructions,
  EssentialProcessingInstructionsHandler,
} from '../components/EssentialProcessingInstructions';
import {useDispatch, useSelector} from 'react-redux';
import {noop} from 'lodash';
import MultiCustodianStructureTips from './MultiCustodianStructureTips';
import AddCustodianModel from './AddCustodianModel';
import {
  CustodianDto,
  CustodianPayload,
  DuplicateNameResponse,
  FieldName,
  FormErrors,
  FormValues,
  IntakeCompletionOption,
  ProcessingSetting,
  ProcessingSettingsData,
  SourceDetailsTabProps,
  ToastState,
} from '../../../../types/upload-new-eviidence/evidenceSourceTypes';
import AurSlateEditor, {
  TermData,
} from '../../search-term-report/tabs/terms-content/AurSlateEditor';
import {RootState} from '../../../../redux/store';
import {useEvidenceCustodian} from '../../../../hooks/upload-evidence-source/useEvidenceCustodian';
import {
  ALPHANUMERIC_DASH_UNDERSCORE_SPACE,
  NO_SPECIALCHAR,
} from '../../../../constants/constants';
import {AurSelect, Option} from '../../../common/aur-select/AurSelect';
import {
  INewUploadEvidence,
  setEvidenceSourceDetails,
  setEvidenceSourceSessionId,
} from '../../../../redux/slices/uploadEvidenceSourceSlice';
import {AurTextField} from '../../../common/aur-text-field/AurTextField';
import AurButton from '../../../common/aur-button/AurButton';
import AurIcon, {IconColors, IconNames} from '../../../common/aur-icon/AurIcon';
import AurToast from '../../../common/aur-toast/AurToast';
import AurTooltip from '../../../common/aur-tooltip/AurTooltip';
import {useEvidenceSource} from '../../../../hooks/upload-evidence-source/useEvidenceSource';
import {showErrorToast} from '../../../../api/utils';
import {AxiosError} from 'axios';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import {getProcessingSettings} from '../../../../api/EvidenceSourceUpload';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {
  PAGE_NAME,
  usePermissionsByPage,
} from '../../../../hooks/permissions/usePermissionsByPage';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

const MAX_LENGTH: Record<FieldName, number> = {
  sourceName: 50,
  clientReference: 15,
};

const ACTION_VALUE = '__add__';

const INTAKE_COMPLETION: IntakeCompletionOption[] = [
  {
    value: 'ProcessImmediately',
    label: 'Process Immediately',
  },
  {
    value: 'HoldInStaging',
    label: 'Hold in Staging Area',
  },
];

const SourceDetailsTab: React.FC<SourceDetailsTabProps> = ({setActiveStep}) => {
  const processingInstructionsRef =
    useRef<EssentialProcessingInstructionsHandler | null>(null);
  const [values, setValues] = useState<FormValues>({
    sourceName: '',
    clientReference: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [custodian, setCustodian] = useState<CustodianDto>();
  const [custodianError, setCustodianError] = useState<string | undefined>();
  const [custodianMode, setCustodianMode] = useState<string>('single');

  const [intakeOption, setIntakeOption] = useState<IntakeCompletionOption>(
    INTAKE_COMPLETION[0]
  );
  const [passwordList, setPasswordList] = useState<TermData[]>([]);

  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const {trackGAEvent} = useAnalytics();
  const [isProcessingDataLoading, setIsProcessingDataLoading] =
    useState<boolean>(false);
  const [defaultProcessingSettings, setDefaultProcessingSettings] =
    useState<ProcessingSettingsData>({});

  const [addCustodianPopup, setAddCustodianPopup] = useState<boolean>(false);
  const [multiCustodianStructureModel, setMultiCustodianStructureModel] =
    useState<boolean>(false);
  const [selectKey, setSelectKey] = useState(0);
  const dispatch = useDispatch();
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const projectId = useSelector(
    (state: RootState) =>
      state.uploadEvidenceSource.selectedProject?.projectId ?? ''
  );

  const {canInitiateProcessing} = usePermissionsByPage(
    PAGE_NAME.UPLOAD_EVIDENCE_SOURCE,
    projectId
  );

  const {evidenceSourceSessionId, sourceDetails} = useSelector(
    (state: RootState) => state.uploadEvidenceSource
  );

  const {custodians, addNewCustodian, isLoading} = useEvidenceCustodian(
    clientId,
    projectId
  );

  const {showToast} = useToast();
  const {checkEvidenceName, getEvidenceSourceId} = useEvidenceSource();

  useEffect(() => {
    setIntakeOption(
      canInitiateProcessing ? INTAKE_COMPLETION[0] : INTAKE_COMPLETION[1]
    );
  }, [canInitiateProcessing]);

  useEffect(() => {
    if (!evidenceSourceSessionId) {
      const fetchSessionId = async () => {
        try {
          const evidenceSourceSessionId = await getEvidenceSourceId();
          dispatch(setEvidenceSourceSessionId(evidenceSourceSessionId));
        } catch (error) {
          const err = error as AxiosError;
          showErrorToast(showToast, err, 'center');
        }
      };
      fetchSessionId();
    }
  }, [evidenceSourceSessionId, getEvidenceSourceId, dispatch, showToast]);

  useEffect(() => {
    if (sourceDetails) {
      setValues({
        sourceName: sourceDetails.evidenceName,
        clientReference: sourceDetails.clientReference,
      });
      setPasswordList(
        sourceDetails.password
          ? sourceDetails.password.map((value) => {
              return {
                value,
              };
            })
          : []
      );
      setCustodianMode(sourceDetails.custodianType as any);
      setCustodian(sourceDetails.singleCustodian);
      setIntakeOption(sourceDetails.intakeCompletion);
    }
  }, [sourceDetails]);

  const validateField = (
    name: FieldName,
    value: string
  ): string | undefined => {
    if (!value.trim()) {
      return t('uploadEvidence.inputFieldRequired');
    }

    const config = FIELD_VALIDATION_CONFIG[name];

    if (config && !config.pattern.test(value)) {
      return config.message;
    }

    if (value.length > MAX_LENGTH[name]) {
      return `Maximum ${MAX_LENGTH[name]} characters allowed`;
    }

    return undefined;
  };

  const FIELD_VALIDATION_CONFIG: Record<
    FieldName,
    {pattern: RegExp; message: string}
  > = {
    sourceName: {
      pattern: ALPHANUMERIC_DASH_UNDERSCORE_SPACE,
      message: t('uploadEvidence.evidenceSourceCharValidation'),
    },
    clientReference: {
      pattern: NO_SPECIALCHAR,
      message: t('uploadEvidence.clientRefCharValidation'),
    },
  };

  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const handleChange =
    (name: FieldName) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;

      setValues((prev) => ({...prev, [name]: value}));
      setErrors((prev) => ({...prev, [name]: validateField(name, value)}));

      if (name === 'sourceName') handleSourceNameChange(value);
    };

  const handleSourceNameChange = (value: string): void => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    //Initiate validation if user stops typing
    debounceRef.current = setTimeout(async () => {
      const trimmed = value.trim();
      if (!trimmed) return;

      const localError = validateField('sourceName', trimmed);
      if (localError) {
        setErrors((prev) => ({...prev, sourceName: localError}));
        return;
      }

      try {
        const response = await checkEvidenceName(trimmed);

        if (response?.data === true) {
          setErrors((prev) => ({...prev, sourceName: undefined}));
        }
      } catch (error) {
        const err = error as AxiosError<DuplicateNameResponse>;

        if (!err?.response?.data?.data && err?.response?.data?.meta?.message) {
          setErrors((prev) => ({
            ...prev,
            sourceName: err.response?.data.meta.message,
          }));
        } else {
          showErrorToast(showToast, err, 'center');
        }
      }
    }, 1000);
  };

  useEffect(() => {
    if (addCustodianPopup) {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.uploadEvidence.addCustodian'),
          type: GA_CLICK_TYPES.LINK,
          region: GA_REGIONS.TAB,
          action: t(
            'gaTracking.uploadEvidence.uploadEvidenceAddCustodianBegin'
          ),
        },
        subPage: {
          subpage_name: t('gaTracking.uploadEvidence.sourceDetails'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });

      trackGAEvent({
        eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,

        subPage: {
          subpage_name: t('gaTracking.uploadEvidence.addCustodian'),
          subpage_type: GA_SUBPAGE_TYPE.MODAL,
        },
      });
    }
  }, [addCustodianPopup]);

  const handleAddCustodian = (): void => {
    setSelectKey((prev) => prev + 1);
    setAddCustodianPopup(true);
  };

  const handleCustodianChange = (event: SelectChangeEvent<string>): void => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.uploadEvidence.custodianSelect', {type: 'single'}),
        type: GA_CLICK_TYPES.DROPDOWN,
        region: GA_REGIONS.TAB,
        action: t(
          'gaTracking.uploadEvidence.uploadEvidenceSelectedCustodianType'
        ),
      },
      subPage: {
        subpage_name: t('gaTracking.uploadEvidence.sourceDetails'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
    const value = event.target.value;
    if (value === ACTION_VALUE) {
      setSelectKey((prev) => prev + 1);
      setAddCustodianPopup(true);
      setCustodianError(undefined);
      return;
    }
    const selectedCustodian = custodians.find(
      (custodian) => String(custodian.id) === value
    );
    setCustodian(selectedCustodian);
    setCustodianError(undefined);
  };

  const custodianApiOptions: Option[] = custodians.map((custodian) => ({
    key: String(custodian.id),
    value: String(custodian.id),
    label: custodian.displayName,
  }));

  const addCustodianOption: Option = {
    key: 'add',
    value: ACTION_VALUE,
    label: '',
    displayElement: (
      <Box
        className="add-custodian-option-1"
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleAddCustodian();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            handleAddCustodian();
          }
        }}
      >
        <AddIcon aria-hidden="true" />
        <span className="add-custodian-text">
          {t('uploadEvidence.addCustodian')}
        </span>
      </Box>
    ),
  };

  const custodianOptions: Option[] = [
    addCustodianOption,
    ...custodianApiOptions,
  ];

  const getBooleanLabel = (value?: boolean) => {
    if (value === undefined) return value;

    return value
      ? t('evidenceSource.initiateProcess.enabled')
      : t('evidenceSource.initiateProcess.disabled');
  };

  const PROCESSING_SETTINGS: ProcessingSetting[] = [
    {
      id: 'priority',
      label: t('uploadEvidence.processPriority'),
      value: defaultProcessingSettings?.processingPriority ?? '-',
    },
    {
      id: 'location',
      label: t('uploadEvidence.location'),
      value: defaultProcessingSettings?.geography ?? '-',
    },
    {
      id: 'timezone',
      label: t('uploadEvidence.timeZone'),
      value: defaultProcessingSettings?.timezone ?? '-',
    },
    {
      id: 'duplication',
      label: t('uploadEvidence.defaultduplication'),
      value: defaultProcessingSettings?.defaultDeDuplication ?? '-',
    },
    {
      id: 'ocr',
      label: t('uploadEvidence.opticalCharRecognition'),
      value: defaultProcessingSettings?.opticalCharacterRecognition ?? '-',
    },
    {
      id: 'destination',
      label: t('uploadEvidence.destinationReview'),
      value: defaultProcessingSettings?.destinationReviewPlatform ?? '-',
    },
    {
      id: 'coreEci',
      label: t('uploadEvidence.coreEci'),
      value: getBooleanLabel(defaultProcessingSettings?.coreECI) ?? '-',
    },
    {
      id: 'aiInvestigate',
      label: t('uploadEvidence.aiInvestigate'),
      value: getBooleanLabel(defaultProcessingSettings?.aiInvestigate) ?? '-',
    },
    {
      id: 'serviceType',
      label: t('common.projectServiceType'),
      value: defaultProcessingSettings?.auroraType || '-',
    },
  ];

  const submitHandler = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.saveAndContinue'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.TAB,
        action: t(
          'gaTracking.uploadEvidence.uploadEvidenceProvidedSourceDetails'
        ),
        details: t('gaTracking.uploadEvidence.passwordIncluded', {
          count: passwordList.length,
        }),
      },
      subPage: {
        subpage_name: t('gaTracking.uploadEvidence.sourceDetails'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
    const uploadedFiles = processingInstructionsRef.current?.getUploadedData();
    const saveFormData: INewUploadEvidence = {
      evidenceName: values.sourceName,
      clientReference: values.clientReference,
      intakeCompletion: intakeOption,
      password: passwordList?.flatMap((password) => [password.value]),
      custodianType: custodianMode,
      singleCustodian: custodian,
      uploadedManifest: uploadedFiles?.manifestUploadedData,
      uploadedPasskey: uploadedFiles?.passkeysUploadedData,
      handlingInstructions: uploadedFiles?.instructionText,
      isAdditionalInstructions: uploadedFiles?.instructionChecked ?? false,
      defaultProcessingSettings: PROCESSING_SETTINGS,
    };
    dispatch(setEvidenceSourceDetails(saveFormData));
    setActiveStep((prev: number) => prev + 1);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,

      subPage: {
        subpage_name: t('gaTracking.uploadEvidence.review'),
        subpage_type: GA_SUBPAGE_TYPE.TAB,
      },
    });
  };

  const handleSubmitCustodian = async (data: CustodianPayload) => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.add'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t(
          'gaTracking.uploadEvidence.uploadEvidenceAddCustodianComplete'
        ),
      },
      subPage: {
        subpage_name: t('gaTracking.uploadEvidence.addCustodian'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
    const result = await addNewCustodian({
      firstName: data.firstName,
      lastName: data.lastName,
    });

    if (result.success) {
      setToast({
        open: true,
        severity: 'success',
        message: result.message,
      });

      setAddCustodianPopup(false);
    } else {
      setToast({
        open: true,
        severity: 'error',
        message: result.message,
      });
    }
  };

  const isBaseValid =
    Boolean(values.sourceName?.trim()) &&
    Boolean(values.clientReference?.trim()) &&
    !errors.sourceName &&
    !errors.clientReference;

  const isCustodianValid = custodianMode !== 'single' || Boolean(custodian?.id);

  const isFormValid = isBaseValid && isCustodianValid;

  const fetchProcessingSettings = useCallback(async () => {
    setIsProcessingDataLoading(true);
    try {
      const response = await getProcessingSettings(clientId, projectId);

      if (response?.data?.meta?.status === 'Success') {
        setDefaultProcessingSettings(response?.data?.data ?? {});
      }
    } catch (error: unknown) {
      const err = error as AxiosError;
      showErrorToast(showToast, err, 'center');
    } finally {
      setIsProcessingDataLoading(false);
    }
  }, [clientId, projectId, showToast]);

  useEffect(() => {
    if (clientId && projectId) {
      fetchProcessingSettings();
    }
  }, [clientId, projectId]);

  return (
    <>
      <Box className="evidence-page">
        <Box className="evidence-left">
          <Box className="evidence-form">
            <AurTextField
              required
              label={t('uploadEvidence.createEvidenceSourceName')}
              className="text-field"
              variant="filled"
              value={values.sourceName}
              onChange={handleChange('sourceName')}
              error={Boolean(errors.sourceName)}
              helperText={
                errors.sourceName ??
                `${t('uploadEvidence.maxCharCount')}: ${MAX_LENGTH.sourceName}`
              }
              ariaLabel={t('uploadEvidence.createEvidenceSourceName')}
              inputProps={{
                maxLength: MAX_LENGTH.sourceName,
                'data-testid': 'sourceName',
              }}
            />

            <AurTextField
              required
              label={t('uploadEvidence.clientReference')}
              className="text-field"
              variant="filled"
              value={values.clientReference}
              onChange={handleChange('clientReference')}
              error={Boolean(errors.clientReference)}
              helperText={
                errors.clientReference ??
                `${t('uploadEvidence.maxCharCount')}: ${MAX_LENGTH.clientReference}`
              }
              ariaLabel={t('uploadEvidence.clientReference')}
              inputProps={{
                maxLength: MAX_LENGTH.clientReference,
                'data-testid': 'clientReference',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <AurTooltip
                      title={t('evidenceSource.tooltip.clientReference')}
                      aria-label={t('evidenceSource.tooltip.clientReference')}
                      className="typo-text body4_size tooltop-content"
                      trigger="click"
                      tabIndex={0}
                      role="button"
                      slotProps={{
                        popper: {
                          modifiers: [
                            {
                              name: 'offset',
                              options: {
                                offset: [0, -3],
                              },
                            },
                          ],
                        },
                      }}
                    >
                      <span className="info-padding">
                        <AurIcon icon={IconNames.Info} />
                      </span>
                    </AurTooltip>
                  </InputAdornment>
                ),
              }}
            />
            {/* Passwords */}
            <Box className="upload-evidence">
              <Typography
                tabIndex={0}
                className="typo-text subhead3_size"
                role="heading"
                aria-level={3}
              >
                {t('uploadEvidence.passwords')}
              </Typography>

              <AurSlateEditor
                initialListData={passwordList}
                onChange={setPasswordList}
                updateHighlightedState={noop}
                placeholder={false}
              />

              <Typography
                tabIndex={0}
                className="typo-text body4_size psw-helper-text"
              >
                {t('uploadEvidence.passwordInNextLine')}
              </Typography>
            </Box>
            {/* Custodian + Intake Section */}
            <Box className="custodian-intake-section">
              <Box className="custodian-section">
                <Typography
                  tabIndex={0}
                  className="typo-text subhead3_size radio-label-color"
                  role="heading"
                  aria-level={3}
                >
                  {t('uploadEvidence.custodianSelection')}
                </Typography>
                <Box className="radio-group1">
                  {[
                    {value: 'single', label: 'Single Custodian'},
                    {value: 'multi', label: 'Multi-Custodian'},
                  ].map((option) => (
                    <React.Fragment key={option.value}>
                      <FormControlLabel
                        className={`radio-option ${
                          custodianMode === option.value
                            ? 'radio-option-selected'
                            : ''
                        }`}
                        control={
                          <Radio
                            checked={custodianMode === option.value}
                            onChange={() => {
                              setCustodianMode(
                                option.value as 'single' | 'multi'
                              );
                              trackGAEvent({
                                eventName: GA_EVENT_NAMES.CLICK,
                                click: {
                                  name: t(
                                    'gaTracking.uploadEvidence.custodianSelect',
                                    {type: option.value}
                                  ),
                                  type: GA_CLICK_TYPES.RADIO_BUTTON,
                                  region: GA_REGIONS.TAB,
                                  action: t(
                                    'gaTracking.uploadEvidence.uploadEvidenceSelectedCustodianType'
                                  ),
                                },
                                subPage: {
                                  subpage_name: t(
                                    'gaTracking.uploadEvidence.sourceDetails'
                                  ),
                                  subpage_type: GA_SUBPAGE_TYPE.TAB,
                                },
                              });
                            }}
                          />
                        }
                        label={
                          <Typography
                            tabIndex={0}
                            className="typo-text body2_size radio-label-color"
                          >
                            {option.label}
                          </Typography>
                        }
                      />

                      {option.value === 'multi' &&
                        custodianMode === 'multi' && (
                          <Typography
                            tabIndex={0}
                            className="typo-text body3_size multi-custo-desc"
                          >
                            {t('uploadEvidence.multiCustoDesc')}
                            <Link
                              href="#"
                              className="typo-text body3_size link-manifest"
                              data-testid="learn-more"
                              role="button"
                              aria-label={`${t('uploadEvidence.learnMore')} for ${t('uploadEvidence.multiCustodian')}`}
                              onClick={(e) => {
                                e.preventDefault();
                                trackGAEvent({
                                  eventName: GA_EVENT_NAMES.CLICK,
                                  click: {
                                    name: t(
                                      'gaTracking.uploadEvidence.learnMore'
                                    ),
                                    type: GA_CLICK_TYPES.LINK,
                                    region: GA_REGIONS.TAB,
                                    action: t(
                                      'gaTracking.uploadEvidence.uploadEvidenceReviewMultiCustodianStructure'
                                    ),
                                  },
                                  subPage: {
                                    subpage_name: t(
                                      'gaTracking.uploadEvidence.sourceDetails'
                                    ),
                                    subpage_type: GA_SUBPAGE_TYPE.TAB,
                                  },
                                });
                                setMultiCustodianStructureModel(true);
                                trackGAEvent({
                                  eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                                  subPage: {
                                    subpage_name: t(
                                      'gaTracking.uploadEvidence.multiCustodianStructure'
                                    ),
                                    subpage_type: GA_SUBPAGE_TYPE.MODAL,
                                  },
                                });
                              }}
                            >
                              {t('uploadEvidence.learnMore')}
                            </Link>
                          </Typography>
                        )}
                      {option.value === 'single' &&
                        custodianMode === 'single' && (
                          <Box className="custodian-select-wrapper">
                            <AurSelect
                              key={selectKey}
                              label={`${t('uploadEvidence.selectCustodian')} *`}
                              optionList={custodianOptions}
                              isSingleSelection
                              field={{
                                value: custodian?.id
                                  ? String(custodian.id)
                                  : '',
                                onChange: handleCustodianChange,
                              }}
                              menuProps={{
                                PaperProps: {
                                  sx: {
                                    maxHeight: 376,
                                    '& .MuiMenu-list': {
                                      maxHeight: 376,
                                      overflowY: 'auto',
                                    },
                                  },
                                },
                              }}
                              fieldState={{
                                error: custodianError
                                  ? {message: custodianError}
                                  : undefined,
                              }}
                            />
                          </Box>
                        )}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>

              <Box className="intake-section">
                <Typography
                  tabIndex={0}
                  className="typo-text subhead3_size radio-label-color"
                  role="heading"
                  aria-level={3}
                >
                  {t('uploadEvidence.uponIntakeCompletion')}
                </Typography>
                <Box className="radio-group1">
                  {INTAKE_COMPLETION.map((option, index) => (
                    <FormControlLabel
                      key={option.value}
                      className={`radio-option ${
                        intakeOption.value === option.value
                          ? 'radio-option-selected'
                          : ''
                      }`}
                      control={
                        <Radio
                          checked={intakeOption.value === option.value}
                          onChange={() => {
                            setIntakeOption(option);
                            trackGAEvent({
                              eventName: GA_EVENT_NAMES.CLICK,
                              click: {
                                name: option.label,
                                type: GA_CLICK_TYPES.RADIO_BUTTON,
                                region: GA_REGIONS.TAB,
                                action: t(
                                  'gaTracking.uploadEvidence.uploadEvidenceSelectedIntakeOption'
                                ),
                              },
                              subPage: {
                                subpage_name: t(
                                  'gaTracking.uploadEvidence.sourceDetails'
                                ),
                                subpage_type: GA_SUBPAGE_TYPE.TAB,
                              },
                            });
                          }}
                          disabled={!canInitiateProcessing && index == 0}
                        />
                      }
                      label={
                        <Typography
                          tabIndex={0}
                          className={`typo-text body2_size radio-label-color ${!canInitiateProcessing && index == 0 ? `label-disabled` : ``}`}
                        >
                          {option.label}
                        </Typography>
                      }
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            <EssentialProcessingInstructions
              ref={processingInstructionsRef}
              description={t('essentialProcessingInstructions.description')}
              showManifest={true}
              defaultSelected={sourceDetails}
            />
          </Box>
        </Box>

        {/* RIGHT */}
        <Box className="evidence-right">
          <Box className="default-settings-header">
            <GradiantLine aria-hidden="true" />
            <Typography
              tabIndex={0}
              className="typo-text subhead3_size header-title"
              role="heading"
              aria-level={3}
            >
              {t('uploadEvidence.defaultProcesSetting')}
            </Typography>
            <Typography
              tabIndex={0}
              className="typo-text body3_size header-content"
            >
              {t('uploadEvidence.defaultSettingDesc')}
            </Typography>
          </Box>

          <Box className="default-settings-list">
            {PROCESSING_SETTINGS.map((item) => (
              <Box className="default-settings-item" key={item.id}>
                <Typography
                  tabIndex={0}
                  className="typo-text label2_size default-settings-label"
                >
                  {item.label}
                </Typography>
                <Typography
                  tabIndex={0}
                  className="typo-text body2_size default-settings-value"
                >
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box className="new-evidence-action-btn">
        <AurButton
          type="continue"
          label={t('addEditTeamMember.form.saveAndContine')}
          title={t('addEditTeamMember.form.saveAndContine')}
          ariaLabel={t('addEditTeamMember.form.saveAndContine')}
          size="Large"
          onClick={submitHandler}
          endIcon={
            <AurIcon
              icon={IconNames.ChevronRight}
              color={IconColors.Custom}
              customClassName={'button-icon'}
            />
          }
          ariaDescription={t('eca.newExport.cancelHint')}
          disabled={!isFormValid}
        />
      </Box>

      {addCustodianPopup && (
        <AddCustodianModel
          showDialog={addCustodianPopup}
          onCancel={() => {
            setAddCustodianPopup(false);
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: t('gaTracking.global.close'),
                type: GA_CLICK_TYPES.BUTTON,
                region: GA_REGIONS.MODAL,
                action: t(
                  'gaTracking.uploadEvidence.uploadEvidenceAddCustodianClosedModal'
                ),
              },
              subPage: {
                subpage_name: t('gaTracking.uploadEvidence.addCustodian'),
                subpage_type: GA_SUBPAGE_TYPE.MODAL,
              },
            });
          }}
          onOk={handleSubmitCustodian}
          isLoading={isLoading}
        />
      )}

      {multiCustodianStructureModel && (
        <MultiCustodianStructureTips
          showDialog={multiCustodianStructureModel}
          onCancel={() => {
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: t('gaTracking.global.close'),
                type: GA_CLICK_TYPES.BUTTON,
                region: GA_REGIONS.MODAL,
                action: t(
                  'gaTracking.uploadEvidence.uploadEvidenceClosedModal'
                ),
              },
              subPage: {
                subpage_name: t(
                  'gaTracking.uploadEvidence.multiCustodianStructure'
                ),
                subpage_type: GA_SUBPAGE_TYPE.MODAL,
              },
            });
            setMultiCustodianStructureModel(false);
          }}
        />
      )}

      <AurToast
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        hideDuration={5000}
        horizontalPosition="center"
        verticalPosition="bottom"
        onClose={() => setToast((prev) => ({...prev, open: false}))}
      />
      <AurLoadingIndicator overlay={true} isOpen={isProcessingDataLoading} />
    </>
  );
};

export default SourceDetailsTab;
